/**
 * 랜딩의 "체류 시계" — 스크롤 진행률을 게스트 한 명의 체류 시각으로 바꾸는 순수 함수들.
 *
 * 시각은 체크인 당일 00:00 을 0 으로 둔 분 단위다(D-3 14:02 → -3478). 정거장 사이에서는
 * 시계가 가운데 40% 구간에서만 흐르고 앞뒤 30% 는 정거장 시각에 머문다 — 정거장 글을 읽는
 * 동안 숫자가 계속 바뀌면 읽을 수가 없어서.
 */

export type StayStop = {
  /** 체크인 당일(D-day) 기준 날짜 차. -3 = 체크인 3일 전. */
  day: number;
  /** 그날 0시부터의 분. */
  minute: number;
  /** 밤 정거장이면 스토리 구간이 밤 톤으로 바뀐다. */
  night?: boolean;
};

const DAY = 24 * 60;

export const stopMinutes = (stop: StayStop): number => stop.day * DAY + stop.minute;

/** 0..1 구간 밖은 끝값으로 붙이고 가운데만 부드럽게 넘어간다. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * 스토리 목록의 ScrollTrigger 진행률(목록 top 이 화면 중앙 → bottom 이 화면 중앙)을
 * 정거장 좌표로 바꾼다. 화면 중앙이 k 번째 정거장 블록의 중심에 오면 position === k.
 */
export function stopPosition(progress: number, count: number): number {
  if (count <= 1) return 0;
  const p = Math.min(1, Math.max(0, progress));
  return Math.min(count - 1, Math.max(0, p * count - 0.5));
}

/**
 * 화면 가운데에 더 가까운 정거장. 시계·이름·휴대폰 화면·밤 톤이 모두 이 하나를 따른다
 * (시계는 중간 시각을 찍지 않고 정거장이 바뀔 때 세어 넘어간다 — landing-motion.ts).
 */
export const activeStop = (position: number): number => Math.round(Math.max(0, position));

/** 정거장 좌표 → 체류 시각(분). */
export function stayMinutesAt(position: number, stops: readonly StayStop[]): number {
  if (stops.length === 0) return 0;
  const last = stops.length - 1;
  const pos = Math.min(last, Math.max(0, position));
  const i = Math.min(last - 1, Math.floor(pos));
  if (last === 0) return stopMinutes(stops[0]);
  const t = smoothstep(0.3, 0.7, pos - i);
  const a = stopMinutes(stops[i]);
  const b = stopMinutes(stops[i + 1]);
  return Math.round(a + (b - a) * t);
}

/** 체류 시각 → 표기. 날짜는 체크인 당일을 "D-day" 로 쓴다. */
export function formatStay(totalMinutes: number): { day: string; hh: string; mm: string } {
  const day = Math.floor(totalMinutes / DAY);
  const inDay = totalMinutes - day * DAY;
  const hh = String(Math.floor(inDay / 60)).padStart(2, "0");
  const mm = String(inDay % 60).padStart(2, "0");
  const label = day === 0 ? "D-day" : day < 0 ? `D${day}` : `D+${day}`;
  return { day: label, hh, mm };
}
